import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  patentabilityAnalysisService,
  type AnalysisProgress,
  type PartialResult,
} from '../services/patentabilityAnalysisService';

interface PatentabilityAnalysisState {
  progress: AnalysisProgress | null;
  partialResults: PartialResult[];
  report: Record<string, unknown> | null;
  isPolling: boolean;
  isLaunching: boolean;
  error: string | null;
  // Poll timer lives in store state (not a module-level variable) so its
  // lifecycle is visible and reset/stop always clear the right handle.
  pollingTimer: ReturnType<typeof setInterval> | null;
}

interface PatentabilityAnalysisActions {
  launch: (subProjectId: string, solutionId: string) => Promise<void>;
  startPolling: (subProjectId: string, solutionId: string, intervalMs?: number) => void;
  stopPolling: () => void;
  fetchPartialResults: (subProjectId: string, solutionId: string) => Promise<void>;
  fetchReport: (subProjectId: string, solutionId: string) => Promise<void>;
  reset: () => void;
}

const initialState: PatentabilityAnalysisState = {
  progress: null,
  partialResults: [],
  report: null,
  isPolling: false,
  isLaunching: false,
  error: null,
  pollingTimer: null,
};

export const usePatentabilityAnalysisStore = create<
  PatentabilityAnalysisState & PatentabilityAnalysisActions
>()(
  devtools(
    (set, get) => ({
      ...initialState,

      launch: async (subProjectId, solutionId) => {
        set({ isLaunching: true, error: null, progress: null, partialResults: [], report: null });
        try {
          await patentabilityAnalysisService.launch(subProjectId, solutionId);
          set({ isLaunching: false });
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : String(err);
          set({ error: msg, isLaunching: false });
          throw err;
        }
      },

      startPolling: (subProjectId, solutionId, intervalMs = 3000) => {
        const prev = get().pollingTimer;
        if (prev) clearInterval(prev);

        let consecutiveFailures = 0;
        const poll = async () => {
          try {
            const [progress, partialResults] = await Promise.all([
              patentabilityAnalysisService.getProgress(subProjectId, solutionId),
              patentabilityAnalysisService.getPartialResults(subProjectId, solutionId),
            ]);
            consecutiveFailures = 0;
            set({ progress, partialResults });
            if (['completed', 'failed'].includes(progress.status)) {
              get().stopPolling();
              if (progress.status === 'completed') {
                get().fetchReport(subProjectId, solutionId);
              }
            }
          } catch (_) {
            // Transient errors tolerated, but not forever: stop and surface after 5 in a row
            // (an expired session or server outage must not become an infinite silent loop).
            consecutiveFailures += 1;
            if (consecutiveFailures >= 5) {
              get().stopPolling();
              set({ error: 'Progress updates failed repeatedly. Reopen this panel to retry.' });
            }
          }
        };

        poll();
        set({ isPolling: true, pollingTimer: setInterval(poll, intervalMs) });
      },

      stopPolling: () => {
        const timer = get().pollingTimer;
        if (timer) clearInterval(timer);
        set({ isPolling: false, pollingTimer: null });
      },

      fetchPartialResults: async (subProjectId, solutionId) => {
        try {
          const partialResults = await patentabilityAnalysisService.getPartialResults(subProjectId, solutionId);
          set({ partialResults });
        } catch (_) {
          // Silently ignore
        }
      },

      fetchReport: async (subProjectId, solutionId) => {
        try {
          const report = await patentabilityAnalysisService.getReport(subProjectId, solutionId);
          set({ report });
        } catch (_) {
          // Report may not exist yet; ignore 404
        }
      },

      reset: () => {
        const timer = get().pollingTimer;
        if (timer) clearInterval(timer);
        set(initialState);
      },
    }),
    { name: 'PatentabilityAnalysisStore' }
  )
);
